// Form state of the entry editor: the fields, the derived duration / rounding
// / amount, validation, rate prefill and saving. One instance per editor.
import '../../messages/entries';
import { entryEditor } from '../../stores/editor.svelte';
import { data, type EntryInput } from '../../stores/data.svelte';
import { account } from '../../stores/account.svelte';
import { prefs } from '../../prefs.svelte';
import { t } from '../../i18n.svelte';
import { toast } from '../../toast.svelte';
import { errorMessage } from '../../api';
import { formatMoney, today, isoDate, parseDate, normalizeStatus } from '../../format';
import { effectiveRounding, roundMinutes, spanMinutes, defaultTimes, prefillRate, timeOf, entryDate, round2, type Status } from '../helpers';

export class EntryForm {
  readonly #uid = Math.random().toString(36).slice(2, 7);

  date = $state(today());
  start = $state('09:00');
  end = $state('17:00');
  pause = $state<number | null>(0);
  clientId = $state<number | null>(null);
  activityId = $state<number | null>(null);
  description = $state('');
  rate = $state<number | null>(null);
  billable = $state(true);
  status = $state<Status>('open');
  amountPaid = $state<number | null>(0);
  invoiceRef = $state('');

  saving = $state(false);
  deleting = $state(false);
  submitted = $state(false);
  rateLoading = $state(false);
  /** The user typed a rate: stop prefilling it. */
  rateTouched = false;
  #rateToken = 0;
  #initial = $state<{ status: Status; amountPaid: number; invoiceRef: string; snapshot: string } | null>(null);

  readonly editing = $derived(entryEditor.entry);
  readonly isEdit = $derived(!!this.editing);

  // ---------- Derived: duration, rounding, amount ----------

  readonly span = $derived(spanMinutes(this.start, this.end));
  readonly overnight = $derived(this.span !== null && this.span < 0);
  readonly gross = $derived(this.span === null ? null : this.overnight ? this.span + 1440 : this.span);
  readonly pauseMin = $derived(Math.max(0, Math.round(Number(this.pause) || 0)));
  readonly worked = $derived(this.gross === null ? null : Math.max(0, this.gross - this.pauseMin));
  readonly client = $derived(this.clientId ? data.clientById.get(this.clientId) ?? null : null);
  readonly rounding = $derived(effectiveRounding(this.client, account.rounding));
  readonly rounded = $derived(this.worked === null ? null : roundMinutes(this.worked, this.rounding));
  readonly roundingDiff = $derived(this.rounded && this.worked !== null ? this.rounded.minutes - this.worked : 0);
  readonly amount = $derived(this.rounded && this.rate ? round2((this.rounded.minutes / 60) * this.rate) : 0);
  readonly outstanding = $derived(this.status === 'paid' || this.status === 'non_billable' ? 0 : Math.max(0, this.amount - (this.amountPaid || 0)));
  readonly nextDay = $derived.by(() => {
    const d = parseDate(this.date);
    return d ? isoDate(new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1)) : '';
  });

  // ---------- Validation ----------

  readonly errors = $derived({
    date: !this.date ? t('entries.err_date') : null,
    start: !this.start ? t('entries.err_time') : null,
    end: !this.end ? t('entries.err_time') : this.span === 0 ? t('entries.err_zero') : this.overnight && this.isEdit ? t('entries.err_end_after_start') : null,
    pause: (Number(this.pause) || 0) < 0 ? t('entries.err_pause') : this.gross !== null && this.gross > 0 && this.pauseMin >= this.gross ? t('entries.err_pause_long') : null,
    client: !this.clientId ? t('entries.err_client') : null,
    rate: this.rate !== null && this.rate < 0 ? t('entries.err_rate') : null,
    amountPaid:
      this.isEdit && this.status !== 'paid' && this.amountPaid !== null && (this.amountPaid < 0 || this.amountPaid > this.amount + 0.009)
        ? t('entries.err_paid_max', { max: formatMoney(this.amount) })
        : null,
    invoice: this.invoiceRef.length > 50 ? t('entries.err_invoice') : null
  });
  readonly hasErrors = $derived(Object.values(this.errors).some(Boolean));
  readonly dirty = $derived(!!this.#initial && entryEditor.isOpen && this.#snapshot() !== this.#initial.snapshot);

  /** Unique id for a field's label/control pair. */
  id(name: string) {
    return `ee-${name}-${this.#uid}`;
  }

  /** The error of a field, once the user tried to save. */
  show(key: keyof EntryForm['errors']) {
    return this.submitted ? this.errors[key] : null;
  }

  #snapshot() {
    return JSON.stringify([this.date, this.start, this.end, this.pause, this.clientId, this.activityId, this.description, this.rate, this.billable, this.status, this.amountPaid, this.invoiceRef]);
  }

  // ---------- Init when the drawer opens ----------

  init() {
    this.submitted = false;
    this.saving = false;
    this.deleting = false;
    this.#rateToken++;
    this.rateLoading = false;
    const e = entryEditor.entry;
    const d = entryEditor.defaults || {};
    const from = e || entryEditor.duplicateOf;
    if (from) {
      this.date = e ? entryDate(from) : today();
      this.start = timeOf(from.start_time) || '09:00';
      this.end = timeOf(from.end_time) || '17:00';
      this.pause = Number(from.pause_minutes) || 0;
      this.clientId = from.client_id;
      this.activityId = from.activity_id;
      this.description = from.description || '';
      this.rate = from.rate === null || from.rate === undefined ? null : Number(from.rate);
      const s = normalizeStatus(from.payment_status);
      this.billable = s !== 'non_billable';
      this.status = e ? s : this.billable ? 'open' : 'non_billable';
      this.amountPaid = e ? Number(from.amount_paid) || 0 : 0;
      this.invoiceRef = e ? from.invoice_reference || '' : '';
      this.rateTouched = true;
    } else {
      this.date = d.date || today();
      const times = defaultTimes(this.date, data.entries, today());
      this.start = d.start_time || times.start;
      this.end = d.end_time || times.end;
      this.pause = d.pause_minutes ?? 0;
      let prefill: number | null = null;
      if (prefs.value.clientPrefillMode === 'remember') prefill = prefs.value.lastUsedClientId;
      else if (prefs.value.clientPrefillMode === 'default') prefill = prefs.value.defaultClientId;
      if (prefill !== null && !data.clientById.get(prefill)) prefill = null;
      this.clientId = d.client_id !== undefined ? d.client_id : prefill;
      this.activityId = d.activity_id ?? null;
      this.description = d.description || '';
      this.rate = d.rate ?? null;
      this.billable = d.billable ?? prefs.value.defaultBillable;
      this.status = this.billable ? 'open' : 'non_billable';
      this.amountPaid = 0;
      this.invoiceRef = '';
      this.rateTouched = d.rate !== undefined && d.rate !== null;
      if (!this.rateTouched) {
        if (this.clientId) this.#prefillRate(this.clientId);
        else if (prefs.value.defaultRate) this.rate = prefs.value.defaultRate;
      }
    }
    this.#initial = { status: this.status, amountPaid: this.amountPaid ?? 0, invoiceRef: this.invoiceRef, snapshot: this.#snapshot() };
  }

  /** Forget the opening state (after save / delete / discard). */
  reset() {
    this.#initial = null;
  }

  // ---------- Client, activity and rate ----------

  async #prefillRate(cid: number) {
    const token = ++this.#rateToken;
    const activity = this.activityId ? data.activityById.get(this.activityId) : null;
    if (activity?.default_rate && activity.default_rate > 0) {
      this.rate = Number(activity.default_rate);
      return;
    }
    this.rateLoading = true;
    try {
      const r = await prefillRate(cid);
      if (token === this.#rateToken && !this.rateTouched) this.rate = r ?? prefs.value.defaultRate ?? this.rate;
    } finally {
      if (token === this.#rateToken) this.rateLoading = false;
    }
  }

  setClient(value: number | string | null) {
    this.clientId = value === null ? null : Number(value);
    if (this.activityId !== null) {
      const a = data.activityById.get(this.activityId);
      if (a && a.client_id !== null && a.client_id !== this.clientId) this.activityId = null;
    }
    if (!this.rateTouched && this.clientId) this.#prefillRate(this.clientId);
  }

  setActivity(value: number | string | null) {
    this.activityId = value === null ? null : Number(value);
    const a = this.activityId ? data.activityById.get(this.activityId) : null;
    if (!this.rateTouched && a?.default_rate && a.default_rate > 0) {
      this.#rateToken++;
      this.rateLoading = false;
      this.rate = Number(a.default_rate);
    }
  }

  // ---------- Billable and payment ----------

  setBillable(on: boolean) {
    this.billable = on;
    if (!on) this.status = 'non_billable';
    else if (this.status === 'non_billable') this.status = 'open';
  }

  setStatus(s: Status) {
    this.status = s;
    this.billable = s !== 'non_billable';
    if (s === 'paid') this.amountPaid = this.amount;
    else if (s === 'open') this.amountPaid = 0;
  }

  // ---------- Save ----------

  /** Marks the form as submitted; false when there are errors (or a save is running). */
  validate(): boolean {
    this.submitted = true;
    return !this.hasErrors && !this.saving;
  }

  /** Creates or updates the entry (overnight new entries become two). True when saved. */
  async save(): Promise<boolean> {
    this.saving = true;
    const base: EntryInput = {
      date: this.date,
      start_time: this.start,
      end_time: this.end,
      client_id: this.clientId,
      activity_id: this.activityId,
      description: this.description.trim() || null,
      rate: this.rate,
      billable: this.billable,
      pause_minutes: this.pauseMin
    };
    try {
      const initial = this.#initial;
      if (this.editing) {
        const patch: Partial<EntryInput> & { amount_paid?: number } = { ...base };
        if (initial && (this.status !== initial.status || (this.amountPaid ?? 0) !== initial.amountPaid)) {
          patch.payment_status = this.status;
          patch.amount_paid = round2(this.amountPaid ?? 0);
        }
        if (initial && this.invoiceRef !== initial.invoiceRef) patch.invoice_reference = this.invoiceRef.trim() || null;
        await data.updateEntry(this.editing.id, patch);
        toast.success(t('entries.updated'));
      } else {
        if (this.overnight) {
          await data.createEntry({ ...base, end_time: '23:59' });
          await data.createEntry({ ...base, date: this.nextDay, start_time: '00:00', pause_minutes: 0 });
          toast.success(t('entries.created_split'));
        } else {
          await data.createEntry(base);
          toast.success(t('entries.created'));
        }
        if (this.clientId && prefs.value.lastUsedClientId !== this.clientId) prefs.update({ lastUsedClientId: this.clientId });
      }
      this.#initial = null;
      return true;
    } catch (err) {
      toast.error(errorMessage(err));
      return false;
    } finally {
      this.saving = false;
    }
  }
}
